import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "ui/button";
import { TextArea } from "ui/text-area";
import toast from "components/partials/toast/toast";

import { useCurrentClient } from "hooks/api/use-clients";
import { usePagePermission } from "hooks/use-page-permission";
import { useParentEmailRules } from "hooks/use-parent-email-rules";

import {
  useDeleteClientConfiguration,
  useSetClientConfiguration,
} from "hooks/api/use-client-configurations";

import { ClientConfigurationDTOConfigKey } from "generated/schemas";

import { getErrorMessage } from "utilities";
import { getClientPaths } from "utilities/app-routes";

import { Permission } from "types/auth";

export const ParentEmailRulesEditPage = () => {
  const client = useCurrentClient();
  const navigate = useNavigate();

  usePagePermission(Permission.PERM_CLIENT_WRITE);

  const parentEmailRules = useParentEmailRules(client.id);
  const setClientConfiguration = useSetClientConfiguration();
  const deleteClientConfiguration = useDeleteClientConfiguration();
  const [aiParentEmailRules, setAiParentEmailRules] = useState("");

  // Sync the hook data with local state for editing
  useEffect(() => {
    setAiParentEmailRules(parentEmailRules);
  }, [parentEmailRules]);

  const handleSave = useCallback(async () => {
    try {
      if (aiParentEmailRules.trim()) {
        await setClientConfiguration.mutateAsync({
          clientId: client.id,
          configKey: ClientConfigurationDTOConfigKey.PARENT_EMAIL_PROMPT,
          configValue: aiParentEmailRules,
        });
        toast.success({
          data: {
            title: "Updated parent email rules",
          },
        });
      } else {
        await deleteClientConfiguration.mutateAsync({
          clientId: client.id,
          configKey: ClientConfigurationDTOConfigKey.PARENT_EMAIL_PROMPT,
        });
        toast.success({
          data: {
            title: "Removed client specific parent email rules",
          },
        });
      }
      const { clientProfilePath } = getClientPaths({ clientId: client.id });
      navigate(clientProfilePath);
    } catch (error) {
      console.error("Failed to save parent email rules:", error);
      toast.error({
        data: {
          title: "Failed to save parent email rules",
          message: getErrorMessage(error?.response?.data),
        },
      });
    }
  }, [aiParentEmailRules, client.id, navigate, setClientConfiguration, deleteClientConfiguration]);

  const handleCancel = useCallback(() => {
    const { clientProfilePath } = getClientPaths({ clientId: client.id });
    navigate(clientProfilePath);
  }, [navigate, client.id]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAiParentEmailRules(event.target.value);
  };

  return (
    <div className="flex flex-col w-[696px] mx-auto mt-5">
      <h1 className="text-3xl text-navy-800 font-bold mb-6">Client parent email rules</h1>
      <p className="mb-2">
        The client parent email rules are a set of client-specific tone and content guidelines that
        are automatically applied during Parent Email generation.
      </p>
      <TextArea value={aiParentEmailRules} onChange={handleChange} rows={13} />
      <div className="flex gap-4 mt-3">
        <Button variant="contained" onClick={handleSave}>
          Save changes
        </Button>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
